/**
 * Meta Ads — a conta conectada, o estado dela e o custo diário por nível.
 *
 * Esta tela responde três perguntas, nessa ordem: **a conta está conectada e viva?**,
 * **quanto saiu de dinheiro, por campanha/conjunto/anúncio?** e **em qual desses números eu
 * já posso confiar?**. Cinco decisões explicam quase tudo que está escrito aqui:
 *
 *  - **O estado vazio é a tela principal, não o consolo.** Ninguém tem conta conectada
 *    ainda, e não vai ter até o App Review da Meta sair — semanas. Então o vazio não é um
 *    "nada aqui": é o passo a passo real (Gerenciador de Negócios, Usuário do Sistema,
 *    Acesso Avançado a `ads_read`, token na variável de ambiente), em português de gente,
 *    com o CTA no fim. Vazio que não ensina é uma tela que passa um mês sem servir para
 *    nada.
 *  - **Token vencido NÃO é tela de erro.** A leitura sai do banco e continua respondendo
 *    com o token morto (`ads.service.ts` não fala com a Meta). O que muda é o aviso: os
 *    números são os mesmos, mas pararam em tal hora, e é isso que a tela diz. Trocar o
 *    painel por um `ErrorState` aqui esconderia justamente o dado que a pessoa precisa ver
 *    para entender por que o gasto "parou".
 *  - **Provisório e definitivo aparecem por PALAVRA e por ÍCONE**, não por cor. Número de
 *    menos de quatro dias ainda muda — a Meta reprocessa —, e mostrar isso como fechado é
 *    mentir com cara de precisão (invariante 9). Quem carimba é o servidor (`is_final`);
 *    esta tela nunca refaz essa conta.
 *  - **Não existe "sincronizar agora"**, e a tela DIZ por quê. Cada consulta gasta a cota da
 *    conta de anúncios do cliente; um botão de atualizar a gastaria a cada visita. A frase
 *    fica visível porque "cadê o botão de atualizar" é a primeira pergunta de quem chega.
 *  - **Nenhum número é calculado aqui** (invariante 3). Gasto, razões e totais chegam
 *    prontos; `null` vira travessão com a frase que explica o que falta acontecer, nunca
 *    zero (invariante 8). A única conta desta tela é dividir centavos por cem.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  EmptyState,
  IconButton,
  ErrorState,
  MetricValue,
  SkeletonRegion,
  SkeletonText,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTrigger,
  useToast,
} from '@mgdlead/ui';
import { CalendarClock, EyeOff, Target } from 'lucide-react';
import { can } from '@mgdlead/shared';

import { FormAlert } from '@mgdlead/ui';
import { NativeSelect } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import {
  ICONE_DESCONECTAR,
  METRICA_ADS,
  NIVEIS,
  NIVEL,
  PERIODOS_ADS,
  PORQUE_NAO_TEM_ATUALIZAR,
  SEM_PERMISSAO_PARA_CONECTAR,
  statusDoCartao,
  caminhoDaEntidade,
  deCentavos,
  diaCurto,
  diaDaConta,
  diaLongo,
  indexarEntidades,
  janelaDeDias,
  nomeDaConta,
  periodoAds,
} from '../../../features/ads/ads-ui';
import type { PeriodoAdsId } from '../../../features/ads/ads-ui';
import { GuiaDeConexao } from '../../../features/guias/GuiaDeConexao';
import { GUIA_META_ADS } from '../../../features/guias/conexoes';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import {
  useAdsConnections,
  useAdsEntities,
  useAdsResumo,
  useAdsSeries,
  useConnectAdsAccount,
  useReplaceAdsToken,
  useRefreshAdsConnections,
  useSyncAdsNow,
  useTestAdsConnection,
  useTestAdsToken,
  useDisconnectAdsAccount,
} from '../../../lib/api';
import type {
  AdsConnection,
  AdsEntity,
  AdsConnectionTest,
  AdsLevel,
  AdsMetricRow,
  AdsResumoRow,
  AdsTotals,
} from '../../../lib/api';
import { ResultadoDoTeste } from './ResultadoDoTeste';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import { ConnectAccountDialog } from './ConnectAccountDialog';
import { VinculoDaCampanha, type CampanhaDaMeta } from './VinculoDaCampanha';

/** A frase que ensina o termo, no cabeçalho da tela. */
const O_QUE_E =
  'Aqui entra o que a Meta cobrou e entregou: a conta de anúncios conectada, a hierarquia dela (campanha, conjunto, anúncio) e o custo de cada dia. Os números são um espelho do que a Meta informa — o sincronizador os traz de hora em hora.';

/**
 * Teto de linhas que esta tela pede.
 *
 * O servidor aceita até 2.000, mas ele soma os TOTAIS sobre as linhas que devolveu: uma
 * série cortada produziria um total menor sem dizer que cortou. Com um teto explícito dá
 * para comparar `rows.length` com ele e avisar — ver `SerieCortada` no fim do arquivo.
 */
const LIMITE_DA_TELA = 500;

export function MetaAdsPage() {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  useDocumentTitle('Meta Ads');

  /**
   * Esconder por `can()` é UX, não segurança — `integration:manage` é conferido no servidor
   * de qualquer jeito. O que se evita é oferecer um botão que só daria erro; quem tem
   * apenas `integration:read` continua vendo conta, estado e números, que é o que importa
   * para entender o custo do dia.
   */
  const podeGerenciar = can(role, 'integration:manage');
  /** Vincular/mover/desvincular campanha da Meta a um bloco é escrita de CAMPANHA (09/10). */
  const podeVincular = can(role, 'campaign:update');
  const [vinculoDe, setVinculoDe] = useState<CampanhaDaMeta | null>(null);

  const [conexaoId, setConexaoId] = useState<string | null>(null);
  const [nivel, setNivel] = useState<AdsLevel>('CAMPAIGN');
  const [periodo, setPeriodo] = useState<PeriodoAdsId>('D7');
  const [conectarAberto, setConectarAberto] = useState(false);
  const [desconectando, setDesconectando] = useState<AdsConnection | null>(null);
  /** A conexão cujo token está sendo trocado (o diálogo de conectar, no modo "trocar"). */
  const [trocandoToken, setTrocandoToken] = useState<AdsConnection | null>(null);
  const [erroDeAcao, setErroDeAcao] = useState<string | null>(null);

  const conexoesQuery = useAdsConnections(workspace.id);
  const conectar = useConnectAdsAccount();
  const trocarToken = useReplaceAdsToken();
  const testarToken = useTestAdsToken();
  const desconectar = useDisconnectAdsAccount();

  const conexoes = useMemo(() => conexoesQuery.data ?? [], [conexoesQuery.data]);
  /** Sem escolha explícita, a primeira — que é a mais recente (o servidor ordena por criação). */
  const selecionada = conexoes.find((c) => c.id === conexaoId) ?? conexoes[0] ?? null;

  /**
   * O dia é o da CONTA DE ANÚNCIO, e o fuso dela é guardado justamente para esta linha.
   * Quando a conta nunca sincronizou ainda não sabemos o fuso: aí vale o do workspace. A tela
   * não fala mais de fuso (pedido do dono, 06/10) — o relógio do topo cumpre esse papel.
   */
  const fuso = selecionada?.accountTimezone ?? workspace.timezone;

  const janela = useMemo(
    () => janelaDeDias(diaDaConta(new Date(), fuso), periodoAds(periodo).dias),
    [fuso, periodo],
  );

  const temConexao = conexoes.length > 0;

  const entidadesQuery = useAdsEntities(workspace.id, selecionada?.id ?? null);
  const porExternalId = useMemo(
    () => indexarEntidades(entidadesQuery.data ?? []),
    [entidadesQuery.data],
  );

  /*
    "Ocultar campanha" — da PESSOA, não do workspace: fica no navegador dela, por conta de
    anúncios, e não muda a visão de ninguém. Guarda as que ela ESCONDEU (e não as que quer
    ver) para campanha nova da Meta aparecer sozinha. O navegador pode recusar o
    armazenamento (aba anônima): aí vale só enquanto a página está aberta.
  */
  const campanhas = useMemo(
    () => (entidadesQuery.data ?? []).filter((e) => e.level === 'CAMPAIGN'),
    [entidadesQuery.data],
  );
  const chaveDasOcultas = selecionada ? `mgdlead:meta-ads:ocultas:${selecionada.id}` : null;
  /** Até 06/10 o filtro guardava as que a pessoa QUERIA ver; convertida uma vez e apagada. */
  const chaveAntiga = selecionada ? `mgdlead:meta-ads:campanhas:${selecionada.id}` : null;
  const [ocultas, setOcultas] = useState<string[]>([]);
  const guardarOcultas = (ids: string[]) => {
    setOcultas(ids);
    if (chaveDasOcultas === null) return;
    try {
      if (ids.length === 0) window.localStorage.removeItem(chaveDasOcultas);
      else window.localStorage.setItem(chaveDasOcultas, JSON.stringify(ids));
    } catch {
      /* sem armazenamento: vale só nesta visita */
    }
  };
  useEffect(() => {
    if (chaveDasOcultas === null) return;
    try {
      const salvo = window.localStorage.getItem(chaveDasOcultas);
      setOcultas(salvo ? (JSON.parse(salvo) as string[]) : []);
    } catch {
      setOcultas([]);
    }
  }, [chaveDasOcultas]);
  useEffect(() => {
    if (chaveAntiga === null || chaveDasOcultas === null || campanhas.length === 0) return;
    try {
      const antigo = window.localStorage.getItem(chaveAntiga);
      if (!antigo) return;
      const queriaVer = new Set(JSON.parse(antigo) as string[]);
      window.localStorage.removeItem(chaveAntiga);
      if (queriaVer.size === 0) return;
      const convertidas = campanhas.map((c) => c.externalId).filter((id) => !queriaVer.has(id));
      setOcultas(convertidas);
      if (convertidas.length > 0)
        window.localStorage.setItem(chaveDasOcultas, JSON.stringify(convertidas));
    } catch {
      /* sem armazenamento: nada a converter */
    }
  }, [chaveAntiga, chaveDasOcultas, campanhas]);

  const aoOcultar = (campanha: { externalId: string; name: string | null }) => {
    const antes = ocultas;
    guardarOcultas([...new Set([...ocultas, campanha.externalId])]);
    toast({
      title: `“${campanha.name ?? campanha.externalId}” saiu da tela`,
      description: 'Só para você. Nada muda na Meta, e dá para mostrar de novo em “Campanhas”.',
      action: { label: 'Desfazer', onClick: () => guardarOcultas(antes) },
    });
  };

  /**
   * Total do período (uma linha por campanha) é o padrão desde 09/10 — pedido do dono: "as
   * campanhas aparecem por dia, mas deveriam aparecer agrupadas e únicas, como na aba
   * Campanhas". O dia a dia continua a um toque, para ver a evolução.
   */
  const [visao, setVisao] = useState<VisaoAds>('TOTAL');
  const resumoQuery = useAdsResumo(
    workspace.id,
    {
      level: nivel,
      since: janela.since,
      until: janela.until,
      connectionId: selecionada?.id ?? null,
      excludeCampaignExternalIds: ocultas,
    },
    temConexao && visao === 'TOTAL',
  );

  const serieQuery = useAdsSeries(
    workspace.id,
    {
      level: nivel,
      since: janela.since,
      until: janela.until,
      connectionId: selecionada?.id ?? null,
      excludeCampaignExternalIds: ocultas,
      limit: LIMITE_DA_TELA,
    },
    temConexao && visao === 'DIA',
  );

  const aoConectar = async (values: { externalAccountId: string; accessToken: string }) => {
    setErroDeAcao(null);
    if (trocandoToken !== null) {
      await trocarToken.mutateAsync({
        workspaceId: workspace.id,
        connectionId: trocandoToken.id,
        accessToken: values.accessToken,
      });
      toast({
        title: 'Token trocado',
        description:
          'A Meta confirmou o token novo. Os números voltam a atualizar na próxima rodada, em até uma hora.',
        tone: 'success',
      });
      return;
    }
    const criada = await conectar.mutateAsync({ workspaceId: workspace.id, ...values });
    setConexaoId(criada.id);
    toast({
      title: 'Conta conectada',
      description:
        'O sincronizador passa nela na próxima rodada. Os primeiros números aparecem aqui em até uma hora.',
      tone: 'success',
    });
  };

  const aoDesconectar = (conexao: AdsConnection) => {
    setErroDeAcao(null);
    desconectar.mutate(
      { workspaceId: workspace.id, connectionId: conexao.id },
      {
        onSuccess: () => {
          setDesconectando(null);
          if (conexaoId === conexao.id) setConexaoId(null);
          toast({
            title: 'Conta desconectada',
            description:
              'As campanhas e as métricas espelhadas saíram junto. Os cliques do seu redirecionador continuam onde estavam.',
            tone: 'success',
          });
        },
        onError: (error: unknown) => setErroDeAcao(humanizeError(error)),
      },
    );
  };

  return (
    <>
      <PageHeader
        title="Meta Ads"
        description={O_QUE_E}
        action={
          /*
            O CTA aparece AQUI só quando já existe conta: sem conta, ele mora dentro do
            estado vazio, junto do passo a passo. Duas cópias do mesmo botão na mesma tela
            disputariam a única vaga de cor de ação (DESIGN_SYSTEM §1.5).
          */
          podeGerenciar && temConexao ? (
            <Button variant="primary" onClick={() => setConectarAberto(true)}>
              Conectar outra conta
            </Button>
          ) : null
        }
      />

      {erroDeAcao !== null ? <FormAlert className="mb-3">{erroDeAcao}</FormAlert> : null}

      {conexoesQuery.isPending ? (
        <SkeletonRegion label="Carregando contas de anúncio" className="flex flex-col gap-4">
          <SkeletonText lines={3} />
          <SkeletonText lines={3} />
        </SkeletonRegion>
      ) : conexoesQuery.isError ? (
        <ErrorState
          title="Não conseguimos carregar as contas de anúncio"
          description={humanizeError(conexoesQuery.error)}
          onRetry={() => void conexoesQuery.refetch()}
        />
      ) : !temConexao ? (
        <ComoConectar podeGerenciar={podeGerenciar} onConectar={() => setConectarAberto(true)} />
      ) : (
        <div className="flex flex-col gap-4">
          <section className="flex flex-col gap-3">
            {conexoes.map((conexao) => (
              <ConexaoCard
                key={conexao.id}
                conexao={conexao}
                timezone={workspace.timezone}
                podeGerenciar={podeGerenciar}
                onDesconectar={() => setDesconectando(conexao)}
                onTrocarToken={() => setTrocandoToken(conexao)}
                workspaceId={workspace.id}
              />
            ))}
            {/* "Cadê o botão de atualizar?" — respondido onde a pergunta nasce. */}
            <p className="max-w-prose text-2xs text-muted-foreground">{PORQUE_NAO_TEM_ATUALIZAR}</p>

            {/*
              Com a conta conectada o guia encolhe para uma linha: quem já conectou vem ver
              número, não tutorial. Mas ele fica AQUI, colado no estado da conexão — é para
              esta tela que a pessoa volta quando o badge vira "Token vencido", e a resposta
              precisa estar onde ela já está olhando.
            */}
            <GuiaDeConexao guia={GUIA_META_ADS} recolhido />
          </section>

          <Serie
            nivel={nivel}
            onNivel={setNivel}
            periodo={periodo}
            onPeriodo={setPeriodo}
            conexoes={conexoes}
            conexaoId={selecionada?.id ?? null}
            onConexao={setConexaoId}
            janela={janela}
            query={serieQuery}
            resumo={resumoQuery}
            visao={visao}
            onVisao={setVisao}
            porExternalId={porExternalId}
            campanhas={campanhas}
            ocultas={ocultas}
            onOcultas={guardarOcultas}
            onOcultar={aoOcultar}
            onVinculo={podeVincular ? setVinculoDe : undefined}
          />
        </div>
      )}

      <VinculoDaCampanha
        workspaceId={workspace.id}
        campanha={vinculoDe}
        onFechar={() => setVinculoDe(null)}
      />

      <ConnectAccountDialog
        open={conectarAberto || trocandoToken !== null}
        onOpenChange={(aberto) => {
          if (!aberto) {
            setConectarAberto(false);
            setTrocandoToken(null);
          }
        }}
        replacing={trocandoToken}
        onSubmit={aoConectar}
        onTest={(values) => testarToken.mutateAsync({ workspaceId: workspace.id, ...values })}
      />

      <Dialog
        open={desconectando !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setDesconectando(null);
        }}
        title="Desconectar conta de anúncios"
        description={
          desconectando ? `A conta ${nomeDaConta(desconectando)} sai deste workspace.` : undefined
        }
        size="sm"
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDesconectando(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={desconectar.isPending}
              loadingLabel="Desconectando"
              onClick={() => desconectando && aoDesconectar(desconectando)}
            >
              Desconectar
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          As campanhas, os conjuntos, os anúncios e as métricas diárias saem junto — eles são uma
          cópia do que está na Meta, e voltam inteiros se você reconectar a conta. Os cliques que o
          seu redirecionador registrou <strong>não</strong> saem: eles aconteceram, e continuam
          contando.
        </p>
      </Dialog>
    </>
  );
}

/* ========================================================================== */
/*  O estado vazio que ensina                                                  */
/* ========================================================================== */

/**
 * O passo a passo real, na ordem.
 *
 * `kind="no-data"` e não `"zero"`: não existe conta cadastrada (ausência estrutural), e não
 * "medimos e deu zero". O passo a passo em si vem inteiro de `GuiaDeConexao` — a lista que
 * morava aqui explicava o CAMINHO e não as PEDRAS, e foi por isso que o próprio dono
 * tropeçou três vezes no mesmo dia. Uma fonte só para esse texto: duas divergem na primeira
 * correção e ninguém sabe qual está na tela.
 *
 * A contagem de passos sai de `GUIA_META_ADS.passos.length` em vez de estar escrita à mão —
 * frase com número fixo mente calada no dia em que alguém acrescenta um passo.
 */
function ComoConectar({
  podeGerenciar,
  onConectar,
}: {
  podeGerenciar: boolean;
  onConectar: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <Card padded={false}>
        <EmptyState
          kind="no-data"
          icon={Target}
          title="Nenhuma conta de anúncios conectada"
          description={`Quando a conta estiver conectada, o gasto de cada campanha, conjunto e anúncio aparece aqui todo dia. São ${GUIA_META_ADS.passos.length} passos até lá — e o último depende da Meta, não de você.`}
        />

        <div className="border-t border-border p-3 md:p-4">
          {podeGerenciar ? (
            /* Ação importante no celular ocupa a largura toda (DESIGN_SYSTEM §11). */
            <Button variant="primary" onClick={onConectar} className="max-md:w-full">
              Conectar conta de anúncios
            </Button>
          ) : (
            <p className="max-w-prose text-sm text-muted-foreground">
              {SEM_PERMISSAO_PARA_CONECTAR}
            </p>
          )}
        </div>
      </Card>

      {/* Sem conta conectada, o guia É a tela — aberto, inteiro, com o "se não funcionar". */}
      <GuiaDeConexao guia={GUIA_META_ADS} />
    </div>
  );
}

/* ========================================================================== */
/*  Uma conexão                                                                */
/* ========================================================================== */

function ConexaoCard({
  conexao,
  timezone,
  podeGerenciar,
  onDesconectar,
  onTrocarToken,
  workspaceId,
}: {
  workspaceId: string;
  conexao: AdsConnection;
  timezone: string;
  podeGerenciar: boolean;
  onDesconectar: () => void;
  onTrocarToken: () => void;
}) {
  const status = statusDoCartao(conexao);
  const testar = useTestAdsConnection();
  /** O último teste DESTE cartão. Some ao recarregar a página — é foto, não estado. */
  const [teste, setTeste] = useState<AdsConnectionTest | null>(null);
  const [erroDoTeste, setErroDoTeste] = useState<string | null>(null);
  /*
    "Sincronizar agora". Depois do pedido, o cartão reconsulta o estado a cada 5 s até a
    leitura TERMINAR (muda `lastSyncAt`) ou FALHAR (muda o erro) — ou até 3 minutos, quando
    para e diz que a leitura segue na fila. Sem isso o botão diria "pedido" e a pessoa
    ficaria sem saber se deu certo.
  */
  const sincronizar = useSyncAdsNow();
  const reconsultar = useRefreshAdsConnections();
  const [espera, setEspera] = useState<{
    lastSyncAt: string | null;
    erro: string | null;
    ate: number;
  } | null>(null);
  const [avisoSync, setAvisoSync] = useState<{
    tone: 'info' | 'warning' | 'success' | 'danger';
    texto: string;
  } | null>(null);

  useEffect(() => {
    if (espera === null) return;
    if (conexao.lastSyncAt !== espera.lastSyncAt) {
      setEspera(null);
      setAvisoSync({
        tone: 'success',
        texto: 'Leitura concluída agora. Os números abaixo já estão atualizados.',
      });
      return;
    }
    if (conexao.lastSyncError !== espera.erro && conexao.lastSyncError !== null) {
      setEspera(null);
      setAvisoSync({ tone: 'danger', texto: conexao.lastSyncError });
      return;
    }
    if (Date.now() > espera.ate) {
      setEspera(null);
      setAvisoSync({
        tone: 'warning',
        texto:
          'A leitura ainda não terminou — ela segue na fila e os números aparecem sozinhos quando concluir.',
      });
      return;
    }
    const t = setTimeout(reconsultar, 5000);
    return () => clearTimeout(t);
  }, [espera, conexao.lastSyncAt, conexao.lastSyncError, reconsultar]);

  const aoSincronizar = () => {
    setAvisoSync(null);
    sincronizar.mutate(
      { workspaceId, connectionId: conexao.id },
      {
        onSuccess: (r) => {
          if (!r.queued) {
            setAvisoSync({
              tone: 'warning',
              texto: `Já pedimos uma leitura desta conta há pouco. Para não gastar a cota da sua conta na Meta, o próximo pedido libera em até ${Math.round(r.cooldownSeconds / 60)} minutos.`,
            });
            return;
          }
          setEspera({
            lastSyncAt: conexao.lastSyncAt,
            erro: conexao.lastSyncError,
            ate: Date.now() + 3 * 60_000,
          });
        },
        onError: (erro: unknown) => setAvisoSync({ tone: 'danger', texto: humanizeError(erro) }),
      },
    );
  };

  const aoTestar = () => {
    setErroDoTeste(null);
    testar.mutate(
      { workspaceId, connectionId: conexao.id },
      {
        onSuccess: setTeste,
        onError: (erro: unknown) => setErroDoTeste(humanizeError(erro)),
      },
    );
  };
  const StatusIcon = status.icon;
  const DesconectarIcon = ICONE_DESCONECTAR;

  const sincronizou = formatRelative(conexao.lastSyncAt);

  return (
    <Card padded={false}>
      <div className="flex flex-col gap-3 p-3 md:flex-row md:items-start md:justify-between md:p-4">
        <div className="min-w-0 flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center gap-1">
            {/* Cor + ícone + palavra: o estado tem que ser legível num print em preto e branco. */}
            <Badge tone={status.tone} icon={<StatusIcon />} title={status.explica}>
              {status.label}
            </Badge>
            <Badge tone="outline">{conexao.externalAccountId}</Badge>
            {conexao.currency !== null ? (
              <Badge tone="outline" numeric>
                {conexao.currency}
              </Badge>
            ) : null}
          </span>

          <h2 className="text-md font-medium">{nomeDaConta(conexao)}</h2>

          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted-foreground">
            {/*
              `null` é "nunca sincronizou", e não "faz muito tempo": a diferença é entre
              esperar a primeira rodada e ir atrás de um problema (invariante 8).
            */}
            <span title={formatExact(conexao.lastSyncAt, timezone) ?? undefined}>
              {conexao.lastSyncAt === null
                ? 'Ainda não sincronizou nenhuma vez'
                : `Última sincronização ${sincronizou ?? 'em data desconhecida'}`}
            </span>
            {conexao.tokenHint !== null ? <span>Token {conexao.tokenHint}</span> : null}
          </p>
        </div>

        <span className="flex flex-col gap-2 md:flex-row md:shrink-0">
          {/* Testar é de todos que leem: é a pergunta "isto funciona?", e não muda credencial. */}
          <Button
            variant="secondary"
            size="sm"
            onClick={aoTestar}
            disabled={testar.isPending}
            aria-label={`Testar a conexão de ${nomeDaConta(conexao)}`}
            className="max-md:w-full"
          >
            {testar.isPending ? 'Testando…' : 'Testar conexão'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={aoSincronizar}
            disabled={sincronizar.isPending || espera !== null}
            aria-label={`Sincronizar agora ${nomeDaConta(conexao)}`}
            className="max-md:w-full"
          >
            {espera !== null ? 'Sincronizando…' : 'Sincronizar agora'}
          </Button>
          {podeGerenciar ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={onTrocarToken}
                aria-label={`Trocar o token de ${nomeDaConta(conexao)}`}
                className="max-md:w-full"
              >
                Trocar token
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={<DesconectarIcon />}
                onClick={onDesconectar}
                aria-label={`Desconectar ${nomeDaConta(conexao)}`}
                className="max-md:w-full md:shrink-0"
              >
                Desconectar
              </Button>
            </>
          ) : null}
        </span>
      </div>

      {espera !== null || avisoSync !== null ? (
        <div className="border-t border-border p-3 md:p-4">
          {espera !== null ? (
            <FormAlert tone="info">
              Leitura pedida — a Meta está sendo consultada. Os números aparecem aqui em 1 a 2
              minutos.
            </FormAlert>
          ) : avisoSync !== null ? (
            <FormAlert tone={avisoSync.tone}>{avisoSync.texto}</FormAlert>
          ) : null}
        </div>
      ) : null}

      {erroDoTeste !== null || teste !== null ? (
        <div className="border-t border-border p-3 md:p-4">
          {erroDoTeste !== null ? (
            <FormAlert tone="warning">{erroDoTeste}</FormAlert>
          ) : teste !== null ? (
            <ResultadoDoTeste teste={teste} />
          ) : null}
        </div>
      ) : null}

      {/*
        O aviso de número parado. É `FormAlert`, não `ErrorState`: o conteúdo da tela
        continua válido e o que se perdeu foi a ATUALIZAÇÃO. Trocar o painel por uma tela de
        erro esconderia os números que a pessoa veio ver.
      */}
      {status.numerosParados ? (
        <div className="flex flex-col gap-2 border-t border-border p-3 md:p-4">
          <FormAlert tone={conexao.status === 'ERROR' ? 'danger' : 'warning'}>
            <span className="block">{status.explica}</span>
            {status.acao !== null ? (
              <span className="mt-1 block">
                <strong>O que resolve: </strong>
                {status.acao}
              </span>
            ) : null}
          </FormAlert>

          {/*
            O conserto mora AO LADO do aviso. Token vencido e credencial ausente se resolvem
            colando um token novo — e o botão do cabeçalho do cartão é fácil de não ver
            quando a pessoa está lendo o vermelho.
          */}
          {podeGerenciar && (conexao.status === 'TOKEN_EXPIRED' || conexao.status === 'ERROR') ? (
            <Button
              variant="primary"
              size="sm"
              onClick={onTrocarToken}
              className="max-md:w-full md:self-start"
            >
              Colar um token novo
            </Button>
          ) : null}

          {/* A mensagem crua do provider fica em segundo plano: serve a quem for investigar. */}
          {conexao.lastSyncError !== null ? (
            <p className="max-w-prose text-2xs text-muted-foreground">
              O que impediu a última leitura: {conexao.lastSyncError}
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/* ========================================================================== */
/*  A série                                                                    */
/* ========================================================================== */

interface SerieProps {
  nivel: AdsLevel;
  onNivel: (nivel: AdsLevel) => void;
  periodo: PeriodoAdsId;
  onPeriodo: (periodo: PeriodoAdsId) => void;
  conexoes: readonly AdsConnection[];
  conexaoId: string | null;
  onConexao: (id: string) => void;
  janela: { since: string; until: string };
  query: ReturnType<typeof useAdsSeries>;
  resumo: ReturnType<typeof useAdsResumo>;
  visao: VisaoAds;
  onVisao: (visao: VisaoAds) => void;
  porExternalId: ReturnType<typeof indexarEntidades>;
  campanhas: readonly AdsEntity[];
  ocultas: readonly string[];
  onOcultas: (ids: string[]) => void;
  onOcultar: (campanha: { externalId: string; name: string | null }) => void;
  /** Abre o vínculo da campanha com o bloco — só para quem pode escrever (09/10). */
  onVinculo?: ((campanha: CampanhaDaMeta) => void) | undefined;
}

function Serie({
  nivel,
  onNivel,
  periodo,
  onPeriodo,
  conexoes,
  conexaoId,
  onConexao,
  janela,
  query,
  resumo,
  visao,
  onVisao,
  porExternalId,
  campanhas,
  ocultas,
  onOcultas,
  onOcultar,
  onVinculo,
}: SerieProps) {
  const [escolhendo, setEscolhendo] = useState(false);
  // A visão escolhida decide qual consulta a seção mostra; a outra nem é pedida ao servidor.
  const ativa = visao === 'TOTAL' ? resumo : query;
  const totais = visao === 'TOTAL' ? resumo.data?.totals : query.data?.totals;
  const linhas = query.data?.rows ?? [];
  const resumoLinhas = resumo.data?.rows ?? [];
  const vazio = visao === 'TOTAL' ? resumoLinhas.length === 0 : linhas.length === 0;

  return (
    <Card padded={false}>
      <div className="flex flex-col gap-3 p-3 md:p-4">
        <CardHeader>
          <CardTitle>{visao === 'TOTAL' ? 'Custo no período' : 'Custo por dia'}</CardTitle>
          <CardDescription>{NIVEL[nivel].explica}</CardDescription>
        </CardHeader>

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <p className="nums text-2xs text-muted-foreground">
            De {diaLongo(janela.since)} a {diaLongo(janela.until)}
          </p>

          <div className="flex flex-col gap-2 sm:flex-row">
            {conexoes.length > 1 ? (
              <label className="flex flex-col gap-1 text-2xs text-muted-foreground">
                Conta
                <NativeSelect
                  size="sm"
                  value={conexaoId ?? ''}
                  onChange={(event) => onConexao(event.target.value)}
                >
                  {conexoes.map((conexao) => (
                    <option key={conexao.id} value={conexao.id}>
                      {nomeDaConta(conexao)}
                    </option>
                  ))}
                </NativeSelect>
              </label>
            ) : null}

            <div className="flex flex-col gap-1 text-2xs text-muted-foreground">
              Campanhas
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEscolhendo(true)}
                disabled={campanhas.length === 0}
              >
                {ocultas.length === 0
                  ? `Todas (${campanhas.length})`
                  : `${ocultas.length} oculta${ocultas.length === 1 ? '' : 's'} · mostrar`}
              </Button>
            </div>

            <label className="flex flex-col gap-1 text-2xs text-muted-foreground">
              Ver
              <NativeSelect
                size="sm"
                value={visao}
                onChange={(event) => onVisao(event.target.value as VisaoAds)}
              >
                <option value="TOTAL">Total do período</option>
                <option value="DIA">Dia a dia</option>
              </NativeSelect>
            </label>

            <label className="flex flex-col gap-1 text-2xs text-muted-foreground">
              Período
              <NativeSelect
                size="sm"
                value={periodo}
                onChange={(event) => onPeriodo(event.target.value as PeriodoAdsId)}
              >
                {PERIODOS_ADS.map((opcao) => (
                  <option key={opcao.id} value={opcao.id}>
                    {opcao.label}
                  </option>
                ))}
              </NativeSelect>
            </label>
          </div>
        </div>
      </div>

      <Tabs value={nivel} onValueChange={(valor) => onNivel(valor as AdsLevel)} className="gap-0">
        {/* A ordem das abas É a hierarquia: conta → campanha → conjunto → anúncio. */}
        <TabsList label="Escolher o nível da hierarquia" className="px-3 md:px-4">
          {NIVEIS.map((opcao) => (
            <TabsTrigger key={opcao} value={opcao}>
              {NIVEL[opcao].plural}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsPanel value={nivel}>
          {ativa.isPending ? (
            <SkeletonRegion
              label="Carregando os números da conta"
              className="flex flex-col gap-4 p-3 md:p-4"
            >
              {[0, 1, 2].map((linha) => (
                <SkeletonText key={linha} lines={2} />
              ))}
            </SkeletonRegion>
          ) : ativa.isError ? (
            <ErrorState
              title="Não conseguimos carregar os números"
              description={humanizeError(ativa.error)}
              onRetry={() => void ativa.refetch()}
            />
          ) : vazio ? (
            /*
              "Sem dados" e não "zero": a série vazia aqui quer dizer que NENHUM dia deste
              recorte foi sincronizado — não que a conta gastou zero. Um `kind="zero"` diria
              "medimos, e deu nada", que é uma afirmação que não temos como fazer.
            */
            <EmptyState
              kind="no-data"
              icon={CalendarClock}
              title="Nenhum dia sincronizado neste período"
              description="O sincronizador ainda não trouxe nada para este recorte. Isso é diferente de ter gastado zero: enquanto não houver rodada, não há o que medir. Se a conta acabou de ser conectada, o primeiro número costuma chegar em até uma hora."
            />
          ) : (
            <>
              {totais ? <Totais totais={totais} /> : null}
              {visao === 'TOTAL' ? (
                <TabelaDoResumo
                  linhas={resumoLinhas}
                  nivel={nivel}
                  porExternalId={porExternalId}
                  janela={janela}
                  onOcultar={nivel === 'CAMPAIGN' ? onOcultar : undefined}
                  onVinculo={nivel === 'CAMPAIGN' ? onVinculo : undefined}
                />
              ) : (
                <>
                  <TabelaDaSerie
                    linhas={linhas}
                    nivel={nivel}
                    porExternalId={porExternalId}
                    janela={janela}
                    onOcultar={nivel === 'CAMPAIGN' ? onOcultar : undefined}
                    onVinculo={nivel === 'CAMPAIGN' ? onVinculo : undefined}
                  />
                  <SerieCortada linhas={linhas.length} />
                </>
              )}
            </>
          )}
        </TabsPanel>
      </Tabs>
      <EscolherCampanhas
        aberto={escolhendo}
        onFechar={() => setEscolhendo(false)}
        campanhas={campanhas}
        ocultas={ocultas}
        onOcultas={onOcultas}
      />
    </Card>
  );
}

/* ========================================================================== */
/*  Os totais do período                                                       */
/* ========================================================================== */

/**
 * Os quatro números do topo — todos vindos prontos do servidor.
 *
 * `currencies` com mais de um valor significa duas contas em moedas diferentes no mesmo
 * recorte: somar sem converter inventaria um número: por isso o gasto vira travessão com a
 * frase, em vez de um total que não existe.
 */
function Totais({ totais }: { totais: AdsTotals }) {
  const moeda = totais.currencies.length === 1 ? totais.currencies[0] : undefined;
  const misturaMoedas = totais.currencies.length > 1;

  return (
    <div className="flex flex-col gap-3 border-b border-border p-3 md:p-4">
      {/* O aviso do período fica ACIMA dos números, não numa legenda de rodapé. */}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label={METRICA_ADS.gasto.label}
          value={misturaMoedas ? null : deCentavos(totais.spendCents)}
          format="currency"
          abbreviate="never"
          {...(moeda ? { currency: moeda } : {})}
          noDataReason={
            misturaMoedas
              ? 'Há mais de uma moeda neste recorte. Somar sem converter inventaria um número — filtre por uma conta de cada vez.'
              : METRICA_ADS.gasto.semDados
          }
        />
        <StatTile
          label={METRICA_ADS.impressoes.label}
          value={totais.impressions}
          noDataReason={METRICA_ADS.impressoes.semDados}
        />
        <StatTile
          label={METRICA_ADS.cliques.label}
          value={totais.clicks}
          noDataReason={METRICA_ADS.cliques.semDados}
          footer={
            /*
              O aviso mais importante desta tela depois do "ainda muda": este clique é o que
              a META contou, e não o clique do redirecionador (`metricas.md §3`). Dois
              números diferentes com o mesmo nome é como se calcula a taxa de entrada contra
              o denominador errado sem ninguém perceber.
            */
            <span className="text-2xs text-muted-foreground">
              Contado pela Meta. Não é o mesmo número dos cliques do seu redirecionador.
            </span>
          }
        />
        <StatTile
          label={METRICA_ADS.cpc.label}
          value={misturaMoedas ? null : deCentavos(totais.cpcCents)}
          format="currency"
          abbreviate="never"
          {...(moeda ? { currency: moeda } : {})}
          noDataReason={
            misturaMoedas
              ? 'Há mais de uma moeda neste recorte, então não há um custo por clique único.'
              : METRICA_ADS.cpc.semDados
          }
        />
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  A tabela                                                                   */
/* ========================================================================== */

/** A visão da tabela: uma linha por item no período (padrão) ou uma por item e dia. */
type VisaoAds = 'TOTAL' | 'DIA';

/**
 * Uma linha por campanha (ou conjunto/anúncio) no período, mais cara primeiro (09/10).
 *
 * Tudo chega somado do servidor (`ads.resumo`) — a tela não soma coluna (invariante 3). O CPC
 * é gasto ÷ cliques DO PERÍODO, não a média dos CPCs diários.
 */
function TabelaDoResumo({
  linhas,
  nivel,
  porExternalId,
  janela,
  onOcultar,
  onVinculo,
}: {
  linhas: readonly AdsResumoRow[];
  nivel: AdsLevel;
  porExternalId: ReturnType<typeof indexarEntidades>;
  janela: { since: string; until: string };
  onOcultar?: ((campanha: { externalId: string; name: string | null }) => void) | undefined;
  onVinculo?: ((campanha: CampanhaDaMeta) => void) | undefined;
}) {
  return (
    <Table
      caption={`Gasto por ${NIVEL[nivel].label.toLowerCase()}, somado de ${diaLongo(janela.since)} a ${diaLongo(janela.until)}.`}
      className="rounded-none border-0"
      containerClassName="rounded-none border-0 border-b border-border"
    >
      <TableHeader>
        <TableRow>
          <TableHead>{NIVEL[nivel].label}</TableHead>
          <TableHead numeric>{METRICA_ADS.gasto.label}</TableHead>
          <TableHead numeric>{METRICA_ADS.impressoes.label}</TableHead>
          <TableHead numeric>{METRICA_ADS.cliques.label}</TableHead>
          <TableHead numeric>{METRICA_ADS.cpc.label}</TableHead>
          <TableHead numeric>Dias</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {linhas.map((linha) => (
          <TableRow key={`${linha.entityId}-${linha.currency}`}>
            <CelulaDoNome
              linha={linha}
              porExternalId={porExternalId}
              onOcultar={onOcultar}
              onVinculo={onVinculo}
            />
            <TableCell numeric>
              <MetricValue
                value={deCentavos(linha.spendCents)}
                format="currency"
                currency={linha.currency}
                abbreviate="never"
                noDataLabel={METRICA_ADS.gasto.semDados}
              />
            </TableCell>
            <TableCell numeric>
              <MetricValue
                value={linha.impressions}
                noDataLabel={METRICA_ADS.impressoes.semDados}
              />
            </TableCell>
            <TableCell numeric>
              <MetricValue value={linha.clicks} noDataLabel={METRICA_ADS.cliques.semDados} />
            </TableCell>
            <TableCell numeric>
              <MetricValue
                value={deCentavos(linha.cpcCents)}
                format="currency"
                currency={linha.currency}
                abbreviate="never"
                noDataLabel={METRICA_ADS.cpc.semDados}
              />
            </TableCell>
            <TableCell
              numeric
              className="nums"
              title={`De ${diaLongo(linha.firstDate)} a ${diaLongo(linha.lastDate)}`}
            >
              {linha.days}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function TabelaDaSerie({
  linhas,
  nivel,
  porExternalId,
  janela,
  onOcultar,
  onVinculo,
}: {
  linhas: readonly AdsMetricRow[];
  nivel: AdsLevel;
  porExternalId: ReturnType<typeof indexarEntidades>;
  janela: { since: string; until: string };
  /** Só no nível campanha: ocultar conjunto/anúncio sem a campanha não teria onde desfazer. */
  onOcultar?: ((campanha: { externalId: string; name: string | null }) => void) | undefined;
  /** Só no nível campanha: o vínculo com o bloco é da CAMPANHA da Meta. */
  onVinculo?: ((campanha: CampanhaDaMeta) => void) | undefined;
}) {
  return (
    <Table
      caption={`Gasto por dia e por ${NIVEL[nivel].label.toLowerCase()}, de ${diaLongo(janela.since)} a ${diaLongo(janela.until)}..`}
      className="rounded-none border-0"
      containerClassName="rounded-none border-0 border-b border-border"
    >
      <TableHeader>
        <TableRow>
          <TableHead>Dia</TableHead>
          <TableHead>{NIVEL[nivel].label}</TableHead>
          <TableHead numeric>{METRICA_ADS.gasto.label}</TableHead>
          <TableHead numeric>{METRICA_ADS.impressoes.label}</TableHead>
          <TableHead numeric>{METRICA_ADS.cliques.label}</TableHead>
          <TableHead numeric>{METRICA_ADS.cpc.label}</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {linhas.map((linha) => (
          <LinhaDaSerie
            key={`${linha.entityId}-${linha.date}`}
            linha={linha}
            porExternalId={porExternalId}
            onOcultar={onOcultar}
            onVinculo={onVinculo}
          />
        ))}
      </TableBody>

      <TableFooter>
        <TableRow>
          {/* O total já veio somado do banco — a tela não soma coluna (invariante 3). */}
          <TableCell colSpan={6} className="py-2 text-2xs text-muted-foreground">
            O total do período está nos cartões acima, somado no servidor sobre este mesmo recorte.
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

/**
 * Nome + caminho + vínculo com o bloco + ocultar — a mesma célula nas duas visões (total e
 * dia a dia), para o "alterar" do bloco estar onde a pessoa estiver olhando.
 */
function CelulaDoNome({
  linha,
  porExternalId,
  onOcultar,
  onVinculo,
}: {
  linha: { externalId: string; name: string | null };
  porExternalId: ReturnType<typeof indexarEntidades>;
  onOcultar?: ((campanha: { externalId: string; name: string | null }) => void) | undefined;
  onVinculo?: ((campanha: CampanhaDaMeta) => void) | undefined;
}) {
  const caminho = caminhoDaEntidade(linha.externalId, porExternalId);
  const linkedTo = porExternalId.get(linha.externalId)?.linkedTo ?? null;

  return (
    <TableCell>
      <span className="flex min-w-0 items-start gap-1">
        <span className="flex min-w-0 flex-1 flex-col">
          {/* Sem nome ainda: o id externo é o que a pessoa consegue procurar na Meta. */}
          <span className="truncate">{linha.name ?? linha.externalId}</span>
          {caminho.length > 0 ? (
            <span className="truncate text-2xs text-muted-foreground">{caminho.join(' ▸ ')}</span>
          ) : null}
          {/*
            O vínculo com o bloco, clicável para quem pode escrever (09/10): é daqui que a
            pessoa percebe que a campanha está no bloco errado, então é daqui que se desfaz.
          */}
          {onVinculo ? (
            <button
              type="button"
              className="self-start truncate text-left text-2xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
              onClick={() =>
                onVinculo({ externalId: linha.externalId, name: linha.name, linkedTo })
              }
            >
              {linkedTo ? `→ bloco “${linkedTo.name}” · alterar` : 'sem bloco · vincular'}
            </button>
          ) : linkedTo ? (
            <span className="truncate text-2xs text-muted-foreground">
              → bloco “{linkedTo.name}”
            </span>
          ) : null}
        </span>
        {onOcultar ? (
          <IconButton
            size="sm"
            variant="ghost"
            icon={<EyeOff />}
            label={`Ocultar “${linha.name ?? linha.externalId}” desta tela`}
            onClick={() => onOcultar({ externalId: linha.externalId, name: linha.name })}
          />
        ) : null}
      </span>
    </TableCell>
  );
}

function LinhaDaSerie({
  linha,
  porExternalId,
  onOcultar,
  onVinculo,
}: {
  linha: AdsMetricRow;
  porExternalId: ReturnType<typeof indexarEntidades>;
  onOcultar?: ((campanha: { externalId: string; name: string | null }) => void) | undefined;
  onVinculo?: ((campanha: CampanhaDaMeta) => void) | undefined;
}) {
  return (
    <TableRow>
      <TableCell nowrap className="nums" title={diaLongo(linha.date)}>
        {diaCurto(linha.date)}
      </TableCell>

      <CelulaDoNome
        linha={linha}
        porExternalId={porExternalId}
        onOcultar={onOcultar}
        onVinculo={onVinculo}
      />

      <TableCell numeric>
        <MetricValue
          value={deCentavos(linha.spendCents)}
          format="currency"
          currency={linha.currency}
          abbreviate="never"
          noDataLabel={METRICA_ADS.gasto.semDados}
        />
      </TableCell>

      <TableCell numeric>
        <MetricValue value={linha.impressions} noDataLabel={METRICA_ADS.impressoes.semDados} />
      </TableCell>

      <TableCell numeric>
        <MetricValue value={linha.clicks} noDataLabel={METRICA_ADS.cliques.semDados} />
      </TableCell>

      <TableCell numeric>
        {/*
          `cpcCents` chega `null` quando não houve clique. Travessão, nunca `R$ 0,00`:
          "custou zero por clique" é uma afirmação sobre desempenho, e não foi isso que
          aconteceu (invariante 8).
        */}
        <MetricValue
          value={deCentavos(linha.cpcCents)}
          format="currency"
          currency={linha.currency}
          abbreviate="never"
          noDataLabel={METRICA_ADS.cpc.semDados}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * O aviso de série cortada.
 *
 * O servidor soma os totais sobre as linhas que devolveu. Se o recorte bateu no teto, o
 * total do topo é menor que o real — e um total menor sem aviso é pior que nenhum total.
 */
function SerieCortada({ linhas }: { linhas: number }) {
  if (linhas < LIMITE_DA_TELA) return null;

  return (
    <div className="p-3 md:p-4">
      <FormAlert tone="warning">
        Este recorte bateu no teto de {LIMITE_DA_TELA} linhas desta tela, então tanto a tabela
        quanto o total acima mostram só o começo do período. Escolha um período menor ou um nível
        mais alto (campanha, em vez de anúncio) para ver o total inteiro.
      </FormAlert>
    </div>
  );
}

/* ========================================================================== */
/*  "As campanhas que eu quero ver"                                            */
/* ========================================================================== */

/**
 * Mostrar/ocultar campanhas. É da PESSOA (guardado no navegador dela): esconde da tabela e
 * dos totais, não do sistema — nada é apagado na Meta nem deixa de sincronizar. Marcada =
 * aparece; a tela guarda as DESMARCADAS, então campanha nova da Meta chega já aparecendo.
 */
function EscolherCampanhas({
  aberto,
  onFechar,
  campanhas,
  ocultas,
  onOcultas,
}: {
  aberto: boolean;
  onFechar: () => void;
  campanhas: readonly AdsEntity[];
  ocultas: readonly string[];
  onOcultas: (ids: string[]) => void;
}) {
  const visiveisAgora = () =>
    new Set(campanhas.map((c) => c.externalId).filter((id) => !ocultas.includes(id)));
  const [marcadas, setMarcadas] = useState<ReadonlySet<string>>(visiveisAgora);
  useEffect(() => {
    if (aberto) setMarcadas(visiveisAgora());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, ocultas, campanhas]);

  const vinculadas = campanhas.filter((c) => c.linkedTo !== null).map((c) => c.externalId);
  const alternar = (id: string) =>
    setMarcadas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  return (
    <Dialog
      open={aberto}
      onOpenChange={(abrir) => {
        if (!abrir) onFechar();
      }}
      title="Campanhas na tela"
      description="Desmarque as que você não quer ver. Vale só para você, neste navegador — nada é apagado na Meta e tudo continua sendo importado."
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOcultas([]);
              onFechar();
            }}
          >
            Mostrar todas
          </Button>
          <Button
            variant="primary"
            block
            disabled={marcadas.size === 0}
            onClick={() => {
              onOcultas(campanhas.map((c) => c.externalId).filter((id) => !marcadas.has(id)));
              onFechar();
            }}
          >
            {marcadas.size === 0
              ? 'Marque ao menos uma'
              : marcadas.size === campanhas.length
                ? 'Ver todas'
                : `Ver ${marcadas.size} · ocultar ${campanhas.length - marcadas.size}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        {vinculadas.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            onClick={() => setMarcadas(new Set(vinculadas))}
          >
            Deixar só as vinculadas a blocos ({vinculadas.length})
          </Button>
        ) : null}
        <ul className="max-h-[50dvh] overflow-y-auto divide-y divide-border rounded-lg border border-border">
          {campanhas.map((c) => (
            <li key={c.externalId} className="flex flex-col gap-1 px-3 py-2">
              <Checkbox
                checked={marcadas.has(c.externalId)}
                onChange={() => alternar(c.externalId)}
                label={c.name ?? c.externalId}
              />
              <span className="flex flex-wrap items-center gap-1 pl-7 text-2xs text-muted-foreground">
                {c.effectiveStatus ? <Badge tone="outline">{c.effectiveStatus}</Badge> : null}
                {c.linkedTo ? <span>vinculada a “{c.linkedTo.name}”</span> : <span>sem bloco</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
